import React, { useState, useEffect, memo, useMemo } from "react";
import { FiltersType } from "../../types";
import { packingFilter } from "../../config";
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  IconButton,
  Badge,
  Chip,
} from "@mui/material";
import {
  FilterListOutlined,
  CloseOutlined,
  ExpandMore,
} from "@mui/icons-material";
import AutocompleteElement from "@atoms/autocomplete";
import DynamicFilters from "@pages/liteWms/components/dynamicFilters";
import {
  FilterElementMapper,
  FiltersState,
} from "@pages/liteWms/helpers/map-filter-element";
import { FilterElementMapper as PLFilterMapper } from "../../helpers/map-filter-element";
import { columnOptions } from "../../helpers/map-cargo-packing";
import { usePageFilterIds } from "@hooks/use-page-filter";

interface OptionType {
  label: string;
  id: number | string;
  section?: string;
}

type FilterComponentProps = {
  columns: string[];
  filters: FiltersType;
  sid: number;
  page: "packing_list" | "lite_wms";
  setFilters: (
    update: FiltersType | ((prev: FiltersType) => FiltersType),
  ) => void;
  setColumns: (update: string[] | ((prev: string[]) => string[])) => void;
  filterObject?: Record<string, any>;
  setFilterObject?: (
    update:
      | Record<string, any>
      | ((prev: Record<string, any>) => Record<string, any>),
  ) => void;
};

const Filters: React.FC<FilterComponentProps> = ({
  filters,
  columns,
  sid,
  page,
  setFilters,
  setColumns,
  filterObject,
  setFilterObject,
}) => {
  const [filterIds, setFiltersIds] = usePageFilterIds(
    page,
    [],
    packingFilter.flatMap((section) => section.filters.map((f) => f.id)),
  );
  const [expanded, setExpanded] = useState<boolean>(false);
  const params = {
    shipping_case_id: sid,
    bill_of_lading__shipping_case_id: sid,
  };
  const TRANSPORT_CONTRACTOR = "transport_contactor";
  const TRANSPORT_CONTRACTOR_CUSTOMER = "transport_contactor_customer";

  const effectiveFilterIds = useMemo(() => {
    const uniqueIds = Array.from(new Set(filterIds));
    const contractorIndex = uniqueIds.indexOf(TRANSPORT_CONTRACTOR);
    const showCustomer =
      (filters.transport_contactor as string[])?.includes("CUSTOMER") &&
      contractorIndex !== -1;

    const next = uniqueIds.filter((id) => id !== TRANSPORT_CONTRACTOR_CUSTOMER);

    if (showCustomer) {
      next.splice(contractorIndex + 1, 0, TRANSPORT_CONTRACTOR_CUSTOMER);
    }

    return next;
  }, [filterIds, filters.transport_contactor]);

  useEffect(() => {
    setFilters((prev: FiltersType) => {
      const updated = { ...prev };
      const activeKeys = new Set(filterIds);

      Object.keys(prev).forEach((key) => {
        if (!activeKeys.has(key) && key !== "id" && key !== "offer_id") {
          delete updated[key];
        }
      });

      return updated;
    });

    setFilterObject?.((prev: Record<string, any>) => {
      const updated = { ...prev };
      const activeKeys = new Set(filterIds);

      Object.keys(prev).forEach((key) => {
        if (!activeKeys.has(key) && key !== "id" && key !== "offer_id") {
          delete updated[key];
        }
      });

      return updated;
    });
  }, [effectiveFilterIds, filterIds, setFilterObject, setFilters]);

  const filtersOptions = [
    { id: "select_all", label: "Select all", section: null },
    ...packingFilter.flatMap((group) =>
      group.filters.map((filter) => ({
        ...filter,
        section: group.section,
      })),
    ),
  ];

  const hasFilter = Object.values(filters).filter(Boolean).length;

  const resetFilters = () => {
    setFilters((prev) =>
      Object.keys(prev).reduce((acc, key) => {
        acc[key] = null;
        return acc;
      }, {} as FiltersType),
    );
    setFilterObject?.({});
  };

  const allColumnOptions = [
    { id: "select_all", label: "Select all" },
    ...columnOptions,
  ];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        gap: 2,
        pt: 1,
        pb: 1,
      }}
    >
      <Accordion
        expanded={expanded}
        onChange={(_, isExpanded) => setExpanded(isExpanded)}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          aria-controls="panel1-content"
          id="panel1-header"
          sx={{ pt: 2, pb: 2 }}
        >
          <Box
            display="flex"
            sx={{ flexDirection: "row", gap: 2, alignItems: "center" }}
          >
            <Typography sx={{ color: "rgba(0, 0, 0, 0.6)" }}>
              Filters:
            </Typography>
            <Badge badgeContent={hasFilter} color="primary">
              <FilterListOutlined sx={{ color: "rgba(0, 0, 0, 0.6)" }} />
            </Badge>
            {columns.length !== columnOptions.length && (
              <Chip
                label={`Selected table columns: ${columns.length}`}
                size="small"
                color="default"
                sx={{
                  border: "1px solid rgba(224, 224, 224, 1)",
                  backgroundColor: "rgba(224, 224, 224, 0.4)",
                }}
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box
            display="grid"
            gridColumn="span 4"
            gridTemplateColumns="repeat(4, 270px)"
            gap={2}
          >
            <DynamicFilters
              filterIds={effectiveFilterIds}
              filters={filters as FiltersState}
              params={params}
              setFilters={setFilters}
              fieldsMapper={{ ...FilterElementMapper, ...PLFilterMapper }}
              filterObject={filterObject}
              setFilterObject={setFilterObject}
            />
            <AutocompleteElement
              options={allColumnOptions}
              label="Table columns"
              size="small"
              multi={true}
              groupBy={(option: any) => option.section}
              value={allColumnOptions.filter((option) =>
                columns.includes(option.id),
              )}
              sx={{ maxWidth: "270px", flex: "1" }}
              onChange={(_: any, value: { id: string }[]) => {
                const isSelectAll = value.some((v) => v.id === "select_all");
                const selected = value.filter((v) => v.id !== "select_all");

                const allRealColumnIds = allColumnOptions
                  .filter((opt) => opt.id !== "select_all")
                  .map((opt) => opt.id);

                const isAllSelected =
                  columns.length === allRealColumnIds.length &&
                  allRealColumnIds.every((id) => columns.includes(id));

                if (isSelectAll && !isAllSelected) {
                  setColumns(allRealColumnIds);
                } else if (isSelectAll && isAllSelected) {
                  setColumns([]);
                } else {
                  setColumns(selected.map((v) => v.id));
                }
              }}
              allOptions={columnOptions}
              selectedIds={columns}
              restricted
            />
            <AutocompleteElement
              options={filtersOptions}
              label="Add filters"
              size="small"
              multi
              groupBy={(option) =>
                typeof option === "string" ? "" : (option.section ?? "")
              }
              value={
                filtersOptions.filter((option) =>
                  filterIds.includes(option.id),
                ) as OptionType[]
              }
              selectedIds={filterIds}
              allOptions={filtersOptions.filter(
                (opt) => opt.id !== "select_all",
              )}
              onChange={(_: any, value: { id: string }[]) => {
                const allIds = filtersOptions
                  .filter((opt) => opt.id !== "select_all")
                  .map((opt) => opt.id);

                const isSelectAll = value.some((v) => v.id === "select_all");

                if (isSelectAll) {
                  const withoutSelectAll = value.filter(
                    (v) => v.id !== "select_all",
                  );
                  const selectedIds = withoutSelectAll.map((v) => v.id);

                  const isAllSelected = selectedIds.length === allIds.length;

                  setFiltersIds(isAllSelected ? [] : allIds);
                } else {
                  const selectedIds = value.map((v) => v.id);
                  setFiltersIds(selectedIds);
                }
              }}
              sx={{ width: "270px", flex: "1" }}
              restricted
            />
          </Box>
          {hasFilter > 0 && expanded && (
            <IconButton
              sx={{
                position: "absolute",
                right: "8px",
                top: "56px",
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              }}
              onClick={resetFilters}
            >
              <CloseOutlined />
            </IconButton>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default memo(Filters);
